import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import {
  UserCheck, Building2, Mail, Phone, MapPin, Users, Hash, MessageSquare,
  Send, Loader2, Check, ArrowRight, Sparkles, ShieldCheck, Clock, Zap, GraduationCap,
} from 'lucide-react';

const REQUEST_TYPES = {
  eleve: {
    label: 'Je suis élève / candidat',
    kicker: 'Élève · Candidat',
    subtitle: 'Une question sur le code, la conduite, ton livret ou le site ? Écris-nous — réponse rapide, gratuite et sans engagement.',
    Icon: GraduationCap,
    color: 'sky',
  },
  ecsr_verification: {
    label: 'Je suis enseignant / enseignante',
    kicker: 'Enseignant ECSR',
    subtitle: 'Active ton profil vérifié en 24 h · Accès complet dashboard multi-élèves.',
    Icon: ShieldCheck,
    color: 'red',
  },
  demo: {
    label: 'Demander une démo',
    kicker: 'Découverte',
    subtitle: '20 min en visio pour te montrer le tableau de bord, les exports REMC et le partage QR.',
    Icon: Sparkles,
    color: 'amber',
  },
  partnership: {
    label: 'Auto-école : équiper mon équipe',
    kicker: 'Partenariat auto-école',
    subtitle: 'Formule multi-postes, formation des moniteurs juniors, activation immédiate.',
    Icon: Building2,
    color: 'emerald',
  },
  other: {
    label: 'Autre demande',
    kicker: 'Question ou proposition',
    subtitle: 'Question, retour d\'expérience, presse, opportunité — écris-moi directement.',
    Icon: MessageSquare,
    color: 'zinc',
  },
};

const ELEVE_SITUATIONS = [
  'Je prépare le code',
  'Je prépare la conduite (B / AAC)',
  'Parent / accompagnateur',
  'Autre',
];

const TEAM_SIZES = [
  { value: '1', label: 'Je suis indépendant · 1 moniteur' },
  { value: '2-5', label: 'Petite équipe · 2 à 5 moniteurs' },
  { value: '6-15', label: 'Auto-école moyenne · 6 à 15 moniteurs' },
  { value: '16+', label: 'Réseau / franchise · 16 moniteurs et +' },
];

const ACCENT_MAP = {
  red: 'from-red-950/40 to-[#0d0d0e] border-red-700/40',
  amber: 'from-amber-950/40 to-[#0d0d0e] border-amber-700/40',
  emerald: 'from-emerald-950/40 to-[#0d0d0e] border-emerald-700/40',
  sky: 'from-sky-950/40 to-[#0d0d0e] border-sky-700/40',
  zinc: 'from-zinc-800/40 to-[#0d0d0e] border-zinc-600/40',
};

export default function DemandeInfoPage() {
  const [params] = useSearchParams();
  const initialType = REQUEST_TYPES[params.get('type')] ? params.get('type') : 'eleve';

  const [requestType, setRequestType] = useState(initialType);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    ecsr_number: '',
    team_size: '',
    city: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const t = params.get('type');
    if (t && REQUEST_TYPES[t]) setRequestType(t);
  }, [params]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      setErr('Nom et email sont obligatoires.');
      return;
    }
    setSending(true); setErr('');
    try {
      await api.submitDemoRequest({ request_type: requestType, ...form });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur lors de l\'envoi. Réessaie ou écris-moi directement.');
    }
    setSending(false);
  };

  const meta = REQUEST_TYPES[requestType];
  const AccentIcon = meta.Icon;
  const cardCls = ACCENT_MAP[meta.color];
  const isEleve = requestType === 'eleve';

  if (success) {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto px-4 lg:px-6 mt-16 sm:mt-24" data-testid="demo-request-success">
          <div className="rounded-3xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 to-[#0d0d0e] p-8 sm:p-14 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center mb-6">
              <Check size={32} className="text-emerald-400" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold mb-3">Message reçu</div>
            <h1 className="font-serif-editorial text-white text-[32px] sm:text-[44px] leading-tight mb-4">
              Merci {form.full_name.split(' ')[0] || ''} !
            </h1>
            <p className="text-white/70 text-[15px] sm:text-[17px] leading-relaxed max-w-xl mx-auto">
              Ta demande vient d&apos;être envoyée. Je te réponds personnellement sous 24 à 48 h ouvrées à <strong className="text-white">{form.email}</strong>.
            </p>
            <p className="text-white/50 text-[13.5px] mt-4">
              Entre-temps, tu peux explorer les <Link to="/enseignant" className="text-emerald-400 hover:text-emerald-300 underline">outils enseignant</Link> ou les <Link to="/demos-interactives" className="text-emerald-400 hover:text-emerald-300 underline">démos interactives</Link>.
            </p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              <Link to="/" className="h-11 px-5 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13.5px] font-semibold inline-flex items-center gap-2">← Retour à l&apos;accueil</Link>
              <Link to="/demos-interactives" className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold inline-flex items-center gap-2">Voir les démos <ArrowRight size={14} /></Link>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12" data-testid="demo-request-page">
        <SectionTitle
          kicker={meta.kicker}
          title={isEleve ? 'Une question ? On t\'écoute.' : 'Parlons de ton projet.'}
          sub={isEleve
            ? 'Élève, candidat ou parent — pose ta question sur le code, la conduite ou le site. Un moniteur ECSR diplômé te répond sous 24-48 h.'
            : 'Auto-école, moniteur indépendant, formateur, réseau — remplis ce formulaire, je te réponds personnellement sous 24-48 h.'}
        />

        {/* Sélecteur de type de demande */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          {Object.entries(REQUEST_TYPES).map(([key, val]) => {
            const Icon = val.Icon;
            const active = requestType === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setRequestType(key)}
                data-testid={`demo-type-${key}`}
                className={`text-left p-4 rounded-2xl border transition ${
                  active
                    ? `bg-gradient-to-br ${ACCENT_MAP[val.color]} border-opacity-100 ring-2 ring-white/10`
                    : 'bg-[#0d0d0e] border-white/10 hover:border-white/30'
                }`}
              >
                <Icon size={18} className={active ? 'text-white' : 'text-white/50'} />
                <div className={`mt-2 text-[13.5px] font-semibold ${active ? 'text-white' : 'text-white/80'}`}>{val.label}</div>
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
          {/* Formulaire */}
          <form onSubmit={submit} className={`rounded-3xl border p-5 sm:p-7 bg-gradient-to-br ${cardCls}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center`}>
                <AccentIcon size={20} className="text-white" />
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/60 font-bold">{meta.kicker}</div>
                <div className="text-white text-[16px] font-semibold">{meta.label}</div>
              </div>
            </div>
            <p className="text-white/60 text-[13.5px] mb-6">{meta.subtitle}</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">Nom complet *</label>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={update('full_name')}
                  data-testid="demo-input-name"
                  placeholder="Jean Dupont"
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update('email')}
                  data-testid="demo-input-email"
                  placeholder="jean@auto-ecole-dupont.fr"
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5 inline-flex items-center gap-1.5"><Phone size={11} /> Téléphone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update('phone')}
                  data-testid="demo-input-phone"
                  placeholder="06 12 34 56 78"
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5 inline-flex items-center gap-1.5"><MapPin size={11} /> Ville</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={update('city')}
                  data-testid="demo-input-city"
                  placeholder="Marseille, Lyon…"
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30"
                />
              </div>
              <div className="sm:col-span-2">
                {isEleve ? (
                  <>
                    <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5 inline-flex items-center gap-1.5"><GraduationCap size={11} /> Ta situation</label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {ELEVE_SITUATIONS.map((s, i) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, role: s }))}
                          data-testid={`eleve-situation-${i}`}
                          className={`text-left p-3 rounded-xl border text-[13px] transition ${
                            form.role === s
                              ? 'bg-sky-500/15 border-sky-400/60 text-white'
                              : 'bg-white/[0.02] border-white/10 text-white/60 hover:text-white hover:border-white/25'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5 inline-flex items-center gap-1.5"><Building2 size={11} /> Auto-école / structure</label>
                    <input
                      type="text"
                      value={form.organization}
                      onChange={update('organization')}
                      data-testid="demo-input-organization"
                      placeholder="Ex : Auto-école Dupont, indépendant…"
                      className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30"
                    />
                  </>
                )}
              </div>
              {!isEleve && (
              <>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">Ton rôle</label>
                <input
                  type="text"
                  value={form.role}
                  onChange={update('role')}
                  data-testid="demo-input-role"
                  placeholder="Moniteur, gérant, formateur…"
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5 inline-flex items-center gap-1.5"><Hash size={11} /> N° ECSR (si applicable)</label>
                <input
                  type="text"
                  value={form.ecsr_number}
                  onChange={update('ecsr_number')}
                  data-testid="demo-input-ecsr"
                  placeholder="Ex : 013-XXXX-01-2020"
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30 font-mono-spec"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5 inline-flex items-center gap-1.5"><Users size={11} /> Effectif</label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {TEAM_SIZES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, team_size: t.value }))}
                      data-testid={`demo-team-${t.value}`}
                      className={`text-left p-3 rounded-xl border text-[13px] transition ${
                        form.team_size === t.value
                          ? 'bg-white/10 border-white/40 text-white'
                          : 'bg-white/[0.02] border-white/10 text-white/60 hover:text-white hover:border-white/25'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              </>
              )}
              <div className="sm:col-span-2">
                <label className="block text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">Ton message</label>
                <textarea
                  value={form.message}
                  onChange={update('message')}
                  data-testid="demo-input-message"
                  rows={5}
                  maxLength={2000}
                  placeholder="Contexte, besoins particuliers, question précise…"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-white/30 focus:outline-none text-white text-[14px] placeholder:text-white/30 resize-none"
                />
                <div className="text-right text-[11px] text-white/40 mt-1">{form.message.length}/2000</div>
              </div>
            </div>

            {err && <div className="mt-4 text-red-400 text-sm">{err}</div>}

            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <button
                type="submit"
                disabled={sending}
                data-testid="demo-submit"
                className="h-12 px-7 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[14px] font-bold inline-flex items-center gap-2 shadow-lg shadow-red-900/30"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                {sending ? 'Envoi en cours…' : 'Envoyer ma demande'}
              </button>
              <div className="text-white/45 text-[12px] inline-flex items-center gap-1.5"><Clock size={11} /> Réponse sous 24-48 h ouvrées</div>
            </div>
          </form>

          {/* Sidebar avantages */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
              <div className="text-[10.5px] uppercase tracking-widest text-red-400 font-bold mb-3 inline-flex items-center gap-1.5"><Zap size={11} /> Réponse rapide garantie</div>
              <h4 className="font-serif-editorial text-white text-[18px] leading-tight mb-3">Ce que tu recevras</h4>
              <ul className="space-y-3">
                {(isEleve ? [
                  { t: 'Réponse d\'un vrai moniteur', d: 'Un moniteur ECSR diplômé te répond personnellement' },
                  { t: 'Conseils concrets', d: 'Code, conduite, AAC, examen — adaptés à ta situation' },
                  { t: '100 % gratuit', d: 'Aucun engagement, aucune carte bancaire' },
                  { t: 'Ressources offertes', d: 'Cours interactifs, tutos vidéo et quiz en libre accès' },
                ] : [
                  { t: 'Réponse personnalisée', d: 'Un email direct de Bruno (fondateur, moniteur ECSR)' },
                  { t: 'Démo adaptée', d: 'On regarde ton besoin réel, pas un tour générique' },
                  { t: 'Activation immédiate', d: 'Compte enseignant vérifié en moins de 24 h' },
                  { t: 'Sans engagement', d: 'Aucune carte bancaire, aucun contrat pour commencer' },
                ]).map((it, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                      <Check size={11} className="text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-[13.5px] font-semibold">{it.t}</div>
                      <div className="text-white/55 text-[12.5px]">{it.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#141416] to-[#0c0c0d] p-5">
              <div className="text-[10.5px] uppercase tracking-widest text-white/50 font-bold mb-3">Préfères tu écrire ?</div>
              <a href="mailto:contact@codevoiture.fr" className="inline-flex items-center gap-2 text-white/85 hover:text-white text-[13.5px] font-semibold">
                <Mail size={14} /> contact@codevoiture.fr
              </a>
              <p className="text-white/45 text-[12px] mt-3 leading-relaxed">
                Ou envoie-moi un message direct via le formulaire — je te réponds depuis mon adresse perso.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}
